// Everything the report builder decides, with no React in it.
//
// Same arrangement as report-shape.js beside it, and for the same reason: there is no DOM
// test harness in this repository and none is being added, so every rule and every
// sentence lives in a pure module that test/web/report-builder.test.js can reach.
//
// THIS MODULE HOLDS NO METRIC, NO DIMENSION, NO GRAIN AND NO TIMEZONE. All of them arrive
// from GET /api/reports/vocabulary, projected by src/reports/vocabulary.js from the same
// frozen maps `compileReport` reads. Two exceptions, both deliberate and both named where
// they appear: the one dimension key a census metric withholds, and the `all` key an
// empty filter set is written with — which is the compiler's shape, not a vocabulary.
// Retyping the vocabulary web-side is how a screen comes to offer a dimension the server
// has never heard of, which is the failure the "served, never retyped" rule prevents.
//
// THE FILTER BUILDER IS THE SAVED-VIEW ONE. A report's `filters` are the SAME object a
// saved view's conditions are, compiled by the same `compileConditions`, so this module
// reuses ../admin/view-conditions.js rather than growing a second condition language.
// It changes exactly one of that module's rulings, and says why below: a view with no
// conditions matches every ticket, but a report with no filters is the ordinary case.
//
// THE WINDOW CONTROL IS report-query.js's. `effectiveWindow` (and the `resolvePreset`
// behind it) and `isValidDay` already decide what "the last 7 days" means for the three
// fixed reports, and a second answer to that question is how a report and its own caption
// come to disagree.

import {
  conditionCount, conditionProblems, conditionsToDraft,
} from '../admin/view-conditions.js';
import { DEFAULT_RANGE, effectiveWindow, isValidDay } from './report-query.js';
import { formatDuration, noteText } from './report-shape.js';

/** What a group with no value at all is called, when the vocabulary names nothing better. */
export const NO_VALUE_LABEL = '(none)';

/** Zero and unknown are different values; this is what unknown looks like. */
export const NO_MEASUREMENT = '—';

const MS_PER_DAY = 86400000;

const isArray = Array.isArray;
const isText = (v) => typeof v === 'string' && v.length > 0;

// ---------------------------------------------------------------------------
// The served vocabulary
// ---------------------------------------------------------------------------

/**
 * The payload of GET /api/reports/vocabulary, or null when the server did not serve one
 * this screen can use.
 *
 * Null is the safe answer and the caller's contract is to say the builder is unavailable.
 * An older `web/dist` deployed against a newer API is a real possibility here, and the
 * failure that matters is the SILENT one: a half-read payload would render as a builder
 * offering no metrics, which looks like a desk that has no reports rather than like a
 * client that could not read the list.
 */
export function reportBuilderVocabulary(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const { metrics, dimensions, grains, window_fields: windowFields, timezones } = payload;
  if (!isArray(metrics) || metrics.length === 0) return null;
  if (!isArray(dimensions) || dimensions.length === 0) return null;
  if (!isArray(grains) || grains.length === 0) return null;
  if (!isArray(windowFields) || windowFields.length === 0) return null;
  if (!isArray(timezones) || timezones.length === 0) return null;
  if (!isText(payload.default_timezone)) return null;
  if (!Number.isInteger(payload.max_window_days) || payload.max_window_days <= 0) return null;
  if (!metrics.every((m) => m && isText(m.key) && isArray(m.aggregates) && m.aggregates.length > 0
    && isText(m.default_aggregate) && isText(m.unit) && typeof m.applies_window === 'boolean')) return null;
  if (!dimensions.every((d) => d && isText(d.key) && typeof d.grainable === 'boolean'
    && typeof d.fans_out === 'boolean')) return null;
  // Returned as served, not copied: the caller hands `vocabulary.filters` straight to
  // ConditionBuilder, and that is the same object the `saved-views` descriptor serves.
  return payload;
}

export function metricFor(key, vocabulary) {
  return (vocabulary?.metrics ?? []).find((metric) => metric.key === key) ?? null;
}

export function dimensionByKey(key, vocabulary) {
  return (vocabulary?.dimensions ?? []).find((dimension) => dimension.key === key) ?? null;
}

/**
 * THE ONE DIMENSION KEY IN THIS FILE, and the only thing it is used for is the rule below.
 * It is a key rather than a served flag because nothing in the registry marks a date
 * column as "the one that records the end of a ticket's life"; adding such a flag would
 * be a registry change to serve one screen. If the key is ever renamed this filter
 * silently becomes a no-op, which fails in the harmless direction: the dimension is
 * offered again, not withheld from a metric that needs it.
 */
const RESOLUTION_DATE = 'resolved_at';

/**
 * The dimensions a metric may be grouped by.
 *
 * A metric with `applies_window: false` is a census of RIGHT NOW: `backlog` counts only
 * tickets that are neither solved nor closed, while the GROUP BY still sees every ticket
 * the filters matched. Grouping that by the resolution date would file every counted
 * ticket under "(none)" and emit one zero-valued row per resolution date in the desk's
 * history — up to the row cap, and every one of them a group the metric cannot measure.
 * MEASURED on the live desk 2026-10-02: no unsolved ticket carries a resolved date.
 *
 * Nothing else is withheld. An unknown metric offers NOTHING rather than everything,
 * because "I do not know what this is" must not read as "anything goes".
 */
export function dimensionsForMetric(metricKey, vocabulary) {
  const metric = metricFor(metricKey, vocabulary);
  if (!metric) return [];
  const all = vocabulary.dimensions;
  return metric.applies_window ? [...all] : all.filter((d) => d.key !== RESOLUTION_DATE);
}

/**
 * The dimensions that may be the SECOND grouping.
 *
 * The same list, less the one already chosen: grouping by a field twice yields a diagonal
 * — every off-diagonal cell empty — which looks like data, and the server answers 422 on
 * `breakdown` for it. NOT a second vocabulary: the breakdown is drawn from the dimensions
 * the server serves, exactly as the dimension is, so there is one list to keep in step.
 *
 * A blank `dimensionKey` still returns the full list rather than nothing: the breakdown
 * control is not offered before a dimension is chosen, and an empty list would be the
 * wrong thing to fall back to if it ever were.
 */
export function breakdownsForMetric(metricKey, dimensionKey, vocabulary) {
  return dimensionsForMetric(metricKey, vocabulary).filter((d) => d.key !== dimensionKey);
}

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------
//
// The draft is the SCREEN's shape, not the wire's: it carries a range preset rather than
// two dates, because "the last 7 days" has to stay a preset while it is being edited, and
// it carries a grain and a window field even when the current metric and dimension have
// no use for them, so switching back does not lose what was chosen.
// `draftToDefinition` is the only thing that turns it into the wire shape, and it sends
// only what the server will actually read.

/**
 * A new draft, with NO DIMENSION CHOSEN.
 *
 * Blank is deliberate and is what makes a fresh builder un-submittable. The alternative —
 * seeding the first dimension — produces a complete, runnable report nobody asked for,
 * and the agent's first act is to notice it is the wrong one. (The condition builder's
 * `newConditionRow` refuses a pre-filled row for the same reason.)
 */
export function newReportDraft(vocabulary) {
  const metric = vocabulary?.metrics?.[0] ?? null;
  return {
    metric: metric?.key ?? '',
    aggregate: metric?.default_aggregate ?? '',
    dimension: '',
    // Blank for the reason the dimension is blank, and for one more: a breakdown is
    // OPTIONAL, so blank is also its finished state. Nothing about a report says it wants
    // two groupings until someone asks for two.
    breakdown: '',
    grain: vocabulary?.grains?.[0] ?? 'day',
    windowField: vocabulary?.window_fields?.[0] ?? 'created_at',
    // The same default the three fixed reports use, for the reason written down there:
    // this desk's records begin in August 2026, so a 30-day window is mostly "not
    // recorded" and reads as broken software rather than as a young desk.
    range: DEFAULT_RANGE,
    from: '',
    to: '',
    tz: vocabulary?.default_timezone ?? '',
    // The shape `compileConditions` insists on, and the shape ConditionBuilder reads.
    filters: { all: [] },
  };
}

/**
 * Changing the metric.
 *
 * The aggregate is RESET rather than kept: `median` means nothing to a count, and the
 * server answers 422 for an aggregate the metric does not declare. The dimension is kept
 * only if the new metric still offers it — carrying one it refuses would leave a draft
 * that looks complete and is not, which is the same quiet rewrite `refieldRow` refuses
 * when a condition's field changes.
 */
export function withMetric(draft, metricKey, vocabulary) {
  const metric = metricFor(metricKey, vocabulary);
  const offered = dimensionsForMetric(metricKey, vocabulary);
  const dimension = offered.some((d) => d.key === draft.dimension) ? draft.dimension : '';
  return {
    ...draft,
    metric: metricKey,
    aggregate: metric?.default_aggregate ?? '',
    dimension,
    // Dropped on the same rule as the dimension, plus the collision rule: if the dimension
    // was cleared and the breakdown survives, a later choice could pick the breakdown's own
    // field as the dimension and the draft would hold the same field twice.
    breakdown: offered.some((d) => d.key === draft.breakdown) && draft.breakdown !== dimension
      ? draft.breakdown
      : '',
  };
}

/**
 * Changing the breakdown. A blank key is how it is REMOVED, which is a real choice and not
 * an unfinished one, so nothing about it is repaired.
 */
export function withBreakdown(draft, breakdownKey, vocabulary) {
  return {
    ...draft,
    breakdown: breakdownKey === draft.dimension ? '' : breakdownKey,
    grain: (vocabulary?.grains ?? []).includes(draft.grain)
      ? draft.grain
      : (vocabulary?.grains?.[0] ?? draft.grain),
  };
}

/**
 * Changing the dimension.
 *
 * The grain is KEPT, not cleared, so toggling between a date dimension and a categorical
 * one does not forget the bucket that was chosen — `draftToDefinition` simply stops
 * sending it. It is repaired only if it is not one of the served grains, which can happen
 * when a stored definition carries a grain the vocabulary has since dropped.
 */
export function withDimension(draft, dimensionKey, vocabulary) {
  return {
    ...draft,
    dimension: dimensionKey,
    // Cleared rather than kept when the two would be the same field: the server refuses
    // that pair, and a draft that silently holds it is a Run button that fails for a
    // reason the agent did not choose.
    breakdown: draft.breakdown === dimensionKey ? '' : draft.breakdown,
    grain: (vocabulary?.grains ?? []).includes(draft.grain)
      ? draft.grain
      : (vocabulary?.grains?.[0] ?? draft.grain),
  };
}

/** The window the report will ACTUALLY ask for, or null when a custom range is half-entered. */
export function draftWindow(draft, today) {
  return effectiveWindow({ range: draft?.range, from: draft?.from, to: draft?.to }, today);
}

/**
 * Whether the window is worth sending at all.
 *
 * Two independent reasons, and the second is easy to miss: a metric that applies the
 * window obviously needs it, but a GRAINABLE DIMENSION needs the window's timezone even
 * when the metric ignores the dates — `compileReport` reads `definition.window?.tz` to
 * decide where a local day starts, and the zone has nowhere else to ride. The server
 * still answers `window_not_applied` in that case, which is the honest thing to say.
 */
function windowApplies(draft, vocabulary) {
  const metric = metricFor(draft.metric, vocabulary);
  const dimension = dimensionByKey(draft.dimension, vocabulary);
  // A grainable BREAKDOWN needs the zone for the same reason a grainable dimension does:
  // `compileReport` binds the window's `tz` whenever EITHER axis buckets a date, and the
  // zone has nowhere else to ride.
  const breakdown = dimensionByKey(draft.breakdown, vocabulary);
  if (!metric) return true;
  return metric.applies_window || dimension?.grainable === true || breakdown?.grainable === true;
}

/** Whether a grain control means anything for this draft: one grain, either axis. */
function grainApplies(draft, vocabulary) {
  return dimensionByKey(draft?.dimension, vocabulary)?.grainable === true
    || dimensionByKey(draft?.breakdown, vocabulary)?.grainable === true;
}

/**
 * The draft as the wire document `validateDefinition` reads.
 *
 * ONLY WHAT THE SERVER WILL READ IS SENT. A grain on a dimension that cannot be bucketed
 * validates and is then ignored by the compiler, which is worse than a refusal: the
 * control would look like it did something. Same for a window on a census metric grouped
 * by a categorical dimension.
 */
export function draftToDefinition(draft, today, vocabulary) {
  const definition = {
    metric: draft.metric,
    aggregate: draft.aggregate,
    dimension: draft.dimension,
  };
  // NOT SENT AT ALL when no breakdown was chosen, rather than sent as null or as the empty
  // string. A definition without the key compiles to byte-identical SQL and is the shape
  // every report saved before this control existed is stored in; a key that is sometimes
  // absent and sometimes null is two shapes for one meaning.
  if (isText(draft.breakdown)) definition.breakdown = draft.breakdown;
  if (grainApplies(draft, vocabulary)) definition.grain = draft.grain;
  definition.filters = draft.filters ?? { all: [] };
  if (windowApplies(draft, vocabulary)) {
    const window = draftWindow(draft, today);
    definition.window = {
      field: draft.windowField,
      // A half-entered range sends its empty halves rather than inventing dates. The
      // builder blocks Run before this is reached; if it is ever reached anyway the
      // server answers 422 naming `window.from`, which is far better than a report for a
      // week nobody chose.
      from: window?.from ?? draft.from ?? '',
      to: window?.to ?? draft.to ?? '',
      tz: draft.tz,
    };
  }
  return definition;
}

/**
 * A stored definition back into a draft.
 *
 * An explicit window becomes a CUSTOM range, never a preset: a report saved for
 * 1 January to 31 March means those dates, and re-reading it as "the last 90 days" would
 * silently answer a different question every time it was opened.
 *
 * Nothing is dropped and nothing is repaired. A dimension this desk no longer has is kept
 * verbatim so the screen can say which one it was, and `draftProblems` refuses to run it —
 * the same ruling `conditionsToDraft` makes for a field the registry does not know.
 */
export function definitionToDraft(definition, vocabulary) {
  const base = newReportDraft(vocabulary);
  if (!definition || typeof definition !== 'object') return base;
  const metric = metricFor(definition.metric, vocabulary);
  const window = definition.window && typeof definition.window === 'object' ? definition.window : null;
  return {
    ...base,
    metric: isText(definition.metric) ? definition.metric : base.metric,
    aggregate: isText(definition.aggregate)
      ? definition.aggregate
      : (metric?.default_aggregate ?? base.aggregate),
    dimension: isText(definition.dimension) ? definition.dimension : '',
    breakdown: isText(definition.breakdown) ? definition.breakdown : '',
    grain: isText(definition.grain) ? definition.grain : base.grain,
    windowField: isText(window?.field) ? window.field : base.windowField,
    range: window ? 'custom' : base.range,
    from: isText(window?.from) ? window.from : '',
    to: isText(window?.to) ? window.to : '',
    tz: isText(window?.tz) ? window.tz : base.tz,
    filters: definition.filters && typeof definition.filters === 'object'
      ? definition.filters
      : { all: [] },
  };
}

// ---------------------------------------------------------------------------
// What is wrong with it
// ---------------------------------------------------------------------------

/**
 * The filter half, which is the condition builder's with ONE ruling changed.
 *
 * `conditionProblems` refuses an EMPTY condition set, because a saved view with no
 * conditions is a named queue showing the entire desk. A report with no filters is the
 * ordinary case — every report is bounded by its window, and "all tickets, grouped by
 * queue" is a question people actually ask — so the empty case is not consulted at all.
 * Everything else (a blank value, a value outside a closed list, too many rows) is the
 * shared module's, unchanged.
 */
function filterProblems(filters, vocabulary) {
  const served = vocabulary?.filters ?? null;
  if (!served) return [];
  const draft = conditionsToDraft(filters, served);
  if (conditionCount(draft) === 0) return [];
  return conditionProblems(draft, served).map((problem) => ({
    field: 'filters',
    message: problem.message,
  }));
}

/**
 * Everything the server would refuse, checked here so the agent reads it beside the
 * control rather than as one 422 for a form with a dozen of them.
 *
 * A deliberate SUBSET of `validateDefinition`: the existence of a custom field, the
 * compile probe and the row cap stay the server's, because they need the database. What
 * is here is what a lead can see on screen and fix without a round trip — and every bound
 * it quotes (`max_window_days`, the grains, the window fields, each metric's aggregates)
 * is the SERVED one, so it stops exactly where the server stops.
 */
export function draftProblems(draft, vocabulary, today) {
  if (!vocabulary) {
    return [{
      field: null,
      message: 'The list of metrics and dimensions could not be loaded, so a report cannot be built. Reload the page.',
    }];
  }
  const metric = metricFor(draft?.metric, vocabulary);
  if (!metric) return [{ field: 'metric', message: 'Choose what to measure.' }];

  const problems = [];
  const offered = dimensionsForMetric(metric.key, vocabulary);
  const dimension = offered.find((d) => d.key === draft.dimension) ?? null;

  if (!isText(draft.dimension)) {
    problems.push({ field: 'dimension', message: 'Choose something to group by.' });
  } else if (!dimension) {
    problems.push({
      field: 'dimension',
      message: dimensionByKey(draft.dimension, vocabulary)
        ? `${metric.label} cannot be grouped by that.`
        : 'This desk no longer has that field, so this report cannot be run. Choose another grouping.',
    });
  }

  if (!metric.aggregates.includes(draft.aggregate)) {
    problems.push({ field: 'aggregate', message: `${metric.label} cannot be summarised that way.` });
  }

  if (isText(draft.breakdown)) {
    const offeredBreakdown = breakdownsForMetric(metric.key, draft.dimension, vocabulary);
    if (draft.breakdown === draft.dimension) {
      problems.push({
        field: 'breakdown',
        message: 'A report cannot be grouped by the same field twice. Choose a different breakdown, or none.',
      });
    } else if (!offeredBreakdown.some((d) => d.key === draft.breakdown)) {
      problems.push({
        field: 'breakdown',
        message: dimensionByKey(draft.breakdown, vocabulary)
          ? `${metric.label} cannot be broken down by that.`
          : 'This desk no longer has that field, so this report cannot be run. Choose another breakdown.',
      });
    }
  }

  if (grainApplies(draft, vocabulary) && !vocabulary.grains.includes(draft.grain)) {
    problems.push({ field: 'grain', message: 'Choose a day, week or month bucket.' });
  }

  if (windowApplies(draft, vocabulary)) {
    if (!vocabulary.window_fields.includes(draft.windowField)) {
      problems.push({ field: 'window', message: 'Choose which date the window measures.' });
    }
    const window = draftWindow(draft, today);
    if (!window) {
      problems.push({ field: 'window', message: 'Choose a start and end date.' });
    } else {
      // Inclusive on both ends, counted the same way `validateWindow` counts it, in UTC
      // space so a DST boundary inside the window cannot move an endpoint by a day.
      const days = Math.round((dayMs(window.to) - dayMs(window.from)) / MS_PER_DAY) + 1;
      if (days > vocabulary.max_window_days) {
        problems.push({
          field: 'window',
          message: `A report can cover at most ${vocabulary.max_window_days} days; this one covers ${days}.`,
        });
      }
    }
  }

  problems.push(...filterProblems(draft.filters, vocabulary));
  return problems;
}

function dayMs(day) {
  return isValidDay(day)
    ? Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)))
    : NaN;
}

export function isSubmittable(draft, vocabulary, today) {
  return draftProblems(draft, vocabulary, today).length === 0;
}

// ---------------------------------------------------------------------------
// What the report has to say about itself BEFORE it is run
// ---------------------------------------------------------------------------

/**
 * The caveats that are knowable from the choice alone.
 *
 * These are the same facts `compileReport` emits as notes WITH the rows; saying them
 * before the report runs is what stops someone reading a multiselect total as a ticket
 * count for the two seconds before the notes arrive. The English is written once, here,
 * and `builderNoteSentences` prints the server's copy afterwards.
 */
export function dimensionWarnings(draft, vocabulary) {
  const metric = metricFor(draft?.metric, vocabulary);
  const dimension = dimensionByKey(draft?.dimension, vocabulary);
  const breakdown = dimensionByKey(draft?.breakdown, vocabulary);
  const warnings = [];

  // BOTH AXES MULTISELECT IS A MULTIPLICATION, NOT TWO INFLATIONS, and the single-field
  // sentence below reads as a bound ("more than the number of tickets") that a reader will
  // use. So it is replaced, not repeated — the same ruling `compileReport` makes when it
  // emits `multiselect_inflates_total_twice` instead of two single-field notes.
  if (dimension?.fans_out && breakdown?.fans_out) {
    warnings.push(
      `${dimension.label} and ${breakdown.label} both allow several values on one ticket. A `
      + 'ticket is counted once for every COMBINATION of its values, so a ticket with three '
      + 'of one and two of the other appears in six cells and the cells add up to a multiple '
      + 'of the number of tickets.',
    );
  } else {
    for (const axis of [dimension, breakdown]) {
      if (axis?.fans_out) {
        warnings.push(
          `${axis.label} allows several values on one ticket. A ticket is counted once `
          + 'under each value it has, so the groups add up to more than the number of tickets.',
        );
      }
    }
  }
  for (const axis of [dimension, breakdown]) {
    if (axis && axis.is_active === false) {
      warnings.push(
        `${axis.label} has been retired. Its stored values are still reported, but no `
        + 'new tickets are getting a value.',
      );
    }
  }
  if (metric && metric.applies_window === false) {
    warnings.push(
      `${metric.label} counts tickets as they are right now, so the date window below is `
      + 'not applied to it.',
    );
  }
  if (metric?.provisional) {
    warnings.push(
      `${metric.label} uses our own definition, which is not confirmed to match Zendesk's`
      + `${isText(metric.definition) ? `: ${metric.definition}` : '.'}`,
    );
  }
  return warnings;
}

/**
 * Every note that has English, deduplicated, in the order the API emitted them.
 *
 * NOT `noteSentences` from report-shape.js, and the difference is a real bug rather than
 * a style preference. The notes POST /run returns carry their own `message`, written
 * beside the SQL fragment that caused them (src/reports/compile.js), and `noteText` knows
 * none of those codes — it would return null for every one and a multiselect report would
 * lose its inflation warning entirely. So the server's sentence wins when there is one,
 * and `noteText` is the fallback for the fixed reports' code-only notes.
 *
 * An unknown code with no message is SILENT rather than rendered as a placeholder.
 */
export function builderNoteSentences(notes) {
  const seen = new Set();
  const out = [];
  for (const note of notes ?? []) {
    if (!note || typeof note !== 'object') continue;
    const text = isText(note.message) && note.message.trim() !== '' ? note.message : noteText(note);
    if (!isText(text) || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Reading the rows
// ---------------------------------------------------------------------------

/**
 * Which shape the result wants.
 *
 *   'timeline'  a date dimension. The rows arrive `ORDER BY dimension_value`, so they are
 *               already chronological, and they MUST STAY that way: sorting a time series
 *               by size draws September before August and calls it a trend.
 *   'ranked'    anything else. One row per value, biggest first, because the question
 *               being asked is "which of these is largest".
 */
export function chartTypeFor(dimensionKey, vocabulary) {
  const dimension = dimensionByKey(dimensionKey, vocabulary);
  if (!dimension) return null;
  return dimension.grainable ? 'timeline' : 'ranked';
}

const measured = (row) => row?.metric_value !== null && row?.metric_value !== undefined
  && Number.isFinite(Number(row.metric_value));

/**
 * A copy in display order. Never sorts in place: the caller's array is the response's.
 *
 * WITH A BREAKDOWN THE SERVER'S ORDER IS KEPT WHATEVER THE CHART TYPE. The rows arrive
 * ordered by dimension and then by breakdown, so every cell of a group is contiguous;
 * ranking cells by size would interleave the groups and the table would stop being a
 * breakdown of anything.
 */
export function orderRows(rows, chartType, hasBreakdown = false) {
  const list = [...(rows ?? [])];
  if (hasBreakdown || chartType !== 'ranked') return list;
  return list.sort((a, b) => {
    // An unmeasured group sorts LAST rather than as zero — it is not a small value, it is
    // the absence of one, and ranking it among the zeros would state the opposite.
    const am = measured(a);
    const bm = measured(b);
    if (am !== bm) return am ? -1 : 1;
    if (am && Number(b.metric_value) !== Number(a.metric_value)) {
      return Number(b.metric_value) - Number(a.metric_value);
    }
    return String(a.dimension_value ?? '').localeCompare(String(b.dimension_value ?? ''));
  });
}

/**
 * One value, as a reader should see it.
 *
 * A fraction is printed as a percentage, which is NOT the thing docs/REPORTS-TAB-DESIGN.md
 * §7 refuses. What is refused there is SLA attainment %, and the stated reason is its
 * DENOMINATOR: `recomputeFirstResponseClock` deletes unsatisfied clocks with no
 * `breached_at` guard, so the set the percentage is taken over silently shrinks and the
 * figure cannot even be bounded. (§5.2 separately refuses a BLENDED attainment, because
 * phone clocks are satisfied at creation by construction.)
 *
 * `one_touch_rate` has neither problem: its denominator is counted in the same statement
 * that produces the figure and arrives as `sample_size`, which this screen prints in its
 * own column beside every value — so the reader can always see what it is a share OF. It
 * also carries the `definition_unconfirmed` caveat, which the notes print.
 */
export function formatMetricValue(value, metric) {
  if (value === null || value === undefined) return NO_MEASUREMENT;
  const n = Number(value);
  if (!Number.isFinite(n)) return NO_MEASUREMENT;
  switch (metric?.unit) {
    case 'minutes':
      // `formatDuration` takes SECONDS; the registry measures these in minutes.
      return formatDuration(n * 60);
    case 'fraction':
      return `${Math.round(n * 1000) / 10}%`;
    default:
      return n.toLocaleString();
  }
}

/**
 * The rows as bars.
 *
 * A COUNT OR A DURATION IS SCALED AGAINST THE LARGEST VALUE IN THE REPORT, so bar length
 * means size. A FRACTION IS SCALED AGAINST THE WHOLE: a desk where every group sits near
 * 20% would otherwise draw one full-width bar and read as a group that is doing well.
 *
 * `width` is a number of percent, rounded to one decimal, and never NaN — an invalid
 * `width: NaN%` declaration is dropped by the browser and the element renders at its
 * CONTENT width, which is how an empty report grows full-width bars.
 */
export function metricBars(rows, metric, chartType, hasBreakdown = false) {
  const ordered = orderRows(rows, chartType, hasBreakdown);
  const values = ordered.filter(measured).map((row) => Number(row.metric_value));
  const scale = metric?.unit === 'fraction'
    ? 1
    : values.reduce((max, v) => (v > max ? v : max), 0);

  return ordered.map((row, index) => {
    const isMeasured = measured(row);
    const value = isMeasured ? Number(row.metric_value) : null;
    const width = isMeasured && scale > 0 && value > 0
      ? Math.min(100, Math.round((value / scale) * 1000) / 10)
      : 0;
    return {
      // The value is the group's identity; the index keeps the key unique when two
      // groups share a label (two users called Pat) or when a value repeats.
      key: `${index}:${row.dimension_value ?? ''}`,
      row,
      value,
      measured: isMeasured,
      formatted: formatMetricValue(row.metric_value, metric),
      sample: Number.isFinite(Number(row.sample_size)) ? Number(row.sample_size) : 0,
      width,
    };
  });
}

/**
 * What a group is called.
 *
 * The server sends `dimension_label` only where the dimension joins a table for one
 * (a user's display name, a queue's name); everything else is its own label and arrives
 * as `dimension_value`. A null value is the dimension's own null label — "Unassigned"
 * rather than "(none)" for an assignee — which is served rather than guessed here.
 *
 * A CUSTOM FIELD'S VALUE IS TRANSLATED THROUGH THE SERVED OPTIONS.
 * `ticket_custom_values.value` holds the ENCODING — a checkbox arrives as the text 'true'
 * or 'false', a select as the option's stored token — and printing either as a group
 * heading is the stored shape leaking onto the screen. GET /vocabulary serves
 * `dimension.options` as `[{value,label}]`, built by the registry's own `format`
 * (src/reports/registry.js), so this module LOOKS THE LABEL UP and never decides it:
 * there is one set of rules for what a stored value is called and it lives beside the
 * compiler. A dimension with no options — every native one, and a select whose
 * `options_json` cannot be read — prints the value as stored, as before.
 */
export function rowLabel(row, dimension) {
  return axisLabel(row?.dimension_value, row?.dimension_label, dimension);
}

/**
 * What a CELL's second axis is called. The same rules, on the other two columns: the
 * breakdown is drawn from the same vocabulary, so it has the same null label, the same
 * checkbox encoding and the same served display name.
 */
export function breakdownLabel(row, breakdown) {
  return axisLabel(row?.breakdown_value, row?.breakdown_label, breakdown);
}

/** The served label for a stored value, or null when this dimension does not name one. */
function optionLabel(dimension, value) {
  if (value === null || value === undefined || value === '') return null;
  const options = dimension?.options;
  if (!Array.isArray(options)) return null;
  const hit = options.find((o) => o && o.value === String(value));
  return hit && isText(hit.label) ? hit.label : null;
}

function axisLabel(value, label, dimension) {
  // FIRST, where the hardcoded checkbox branch used to be: this is the translation of the
  // stored encoding, and a `dimension_label` joined out of a table is never sent for the
  // kinds of dimension that have options.
  const option = optionLabel(dimension, value);
  if (option !== null) return option;
  if (isText(label)) return label;
  if (value === null || value === undefined || value === '') {
    return isText(dimension?.null_label) ? dimension.null_label : NO_VALUE_LABEL;
  }
  return String(value);
}

// ---------------------------------------------------------------------------
// Who a saved report is for
// ---------------------------------------------------------------------------
//
// Three audiences, and they are the SERVER's three — migration 038 stores them as two
// nullable columns (`owner_user_id`, `visible_to_queue_id`) with a CHECK forbidding both
// at once, and POST /api/reports/saved takes them as one discriminated `kind`. This
// module speaks the discriminated shape only. A control that could set an owner AND a
// queue would make the CHECK the thing that reports an error to an agent, which is a
// constraint name arriving where a sentence belongs.

/**
 * The payload of GET /api/reports/saved, or null when the server did not serve one this
 * screen can use — the same contract, and the same reasoning, as
 * `reportBuilderVocabulary`: a half-read payload must say "the list could not be loaded"
 * rather than render as a desk with no saved reports.
 *
 * An EMPTY list is not a failure and must not be confused with one: a desk that has
 * saved nothing yet is the ordinary first case.
 */
export function savedReportList(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (!isArray(payload.data)) return null;
  const queues = isArray(payload.meta?.my_queues) ? payload.meta.my_queues : [];
  return {
    // A row without an id cannot be opened and a row without a name cannot be read, so
    // neither is rendered. Dropped rather than repaired: there is no honest placeholder
    // for the name of a report.
    reports: payload.data.filter((r) => r && isText(r.id) && isText(r.name)),
    queues: queues.filter((q) => q && isText(q.id) && isText(q.name)),
  };
}

/**
 * A fresh audience choice.
 *
 * SHARED for someone who may publish, which is what every report saved before this
 * existed was. PRIVATE for everyone else, because it is the only audience they may save
 * into — a default the server would refuse is a Save button that fails for a reason the
 * agent did not choose.
 *
 * `canPublish` is required rather than defaulted: the permissive value is the dangerous
 * one, and a caller that forgot the argument would get it.
 */
export function newAudienceChoice(canPublish) {
  return { kind: canPublish === true ? 'shared' : 'private', queue_id: '' };
}

/**
 * The audiences this caller may choose, in the order the control offers them.
 *
 * `queue` is DROPPED for a caller who belongs to no queue. An option whose second half
 * cannot be filled in is not a choice — it is a disabled Save button with no explanation
 * — and the server would refuse it anyway, since the queue must be one of the caller's
 * own memberships.
 *
 * `shared` and `queue` are both DROPPED for a caller who may not publish, leaving one
 * choice. A user decision of 2026-10-05 is that an agent may keep a report private to
 * themselves while sharing one with the desk or a queue stays a lead's; this is the
 * PRESENTATION of that rule, and `assertMayPublish` in src/routes/api/reports.js is the
 * enforcement. SPEC.md §3.3: hiding a control is never authorization.
 *
 * `canPublish` is required rather than defaulted, for `newAudienceChoice`'s reason: a
 * missing argument must not read as permission.
 */
export function audienceChoices(queues, canPublish) {
  const choices = [{ value: 'private', label: 'Only me' }];
  if (canPublish !== true) return choices;
  // Shared first once it is offered at all: it is the default, and a list whose first
  // entry is not the default reads as though the default were something else.
  choices.unshift({ value: 'shared', label: 'Everyone on the desk' });
  if (isArray(queues) && queues.length > 0) {
    choices.push({ value: 'queue', label: 'A queue I belong to' });
  }
  return choices;
}

/**
 * The `audience` a Save should send, or NULL when the choice is not finished.
 *
 * Null is what disables Save. The queue is checked against the SERVED memberships rather
 * than merely being non-empty: the server refuses a queue the caller does not belong to
 * with a 422 on `audience.queue_id`, and a control that can only offer the caller's own
 * queues should never be what produces that refusal.
 */
export function audienceToWire(choice, queues) {
  const kind = choice?.kind;
  if (kind === 'shared' || kind === 'private') return { kind };
  if (kind !== 'queue') return null;
  const id = String(choice?.queue_id ?? '');
  if (!(isArray(queues) && queues.some((q) => String(q.id) === id))) return null;
  return { kind: 'queue', queue_id: id };
}

/**
 * What a saved report's audience says to whoever is reading the list.
 *
 * "Only you" rather than "Private": every row in this list reached the caller through
 * the audience clause, so a row marked private IS the caller's own — there is no other
 * private row they could be looking at.
 *
 * The unnamed-queue fallback is not expected to render: the list and the memberships
 * arrive in one response, and a queue-scoped row is only in that list because the caller
 * is in that queue. It is there so a mismatch prints a true, vaguer sentence instead of
 * the word "undefined".
 */
export function audienceSentence(audience, queues) {
  const kind = audience?.kind;
  if (kind === 'private') return 'Only you';
  if (kind === 'queue') {
    const queue = (isArray(queues) ? queues : [])
      .find((q) => String(q.id) === String(audience?.queue_id));
    return queue ? `Shared with ${queue.name}` : 'Shared with one queue';
  }
  return 'Shared with everyone';
}
